import { memo, useEffect, useState, useRef, Fragment } from 'react';
import { Handle, Position } from '@xyflow/react';
import ContentEditable from 'react-contenteditable';
import DOMPurify from 'dompurify';
import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useTranslation } from '../../hooks/useTranslation';
import { stripHtml } from '../../components/StripHtml';
import ActivityEnergyLayer, {
  getEnergyMeterClass,
} from '../../components/energy-meter/ActivityEnergyLayer';
import { hyphenateText, cleanHyphens } from '../../utils/hyphenateText';

const SwimlaneBoxNode = ({ id, data, langCode, editable }) => {
  const [title, setTitle] = useState(() =>
    hyphenateText(stripHtml(data?.details?.title || data?.label || ''), langCode)
  );
  const boxRef = useRef(null);
  const [autoFocus, setAutoFocus] = useState(data.autoFocus);
  const [isHovered, setIsHovered] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupSize, setPopupSize] = useState({ width: 600, height: 450 });
  const [maxConstraints, setMaxConstraints] = useState([1460, 560]);
  const t = useTranslation();

  const nodeId = id || data.id;
  const em = data?.em;
  const emClass = getEnergyMeterClass(em, nodeId);

  // Sync constraints
  useEffect(() => {
    const updateMaxConstraints = () => {
      setMaxConstraints([1460, 560]);
    };
    updateMaxConstraints();
    window.addEventListener('resize', updateMaxConstraints);
    return () => window.removeEventListener('resize', updateMaxConstraints);
  }, []);

  // Sync label (only when not focused to preserve cursor and space typing)
  useEffect(() => {
    const isFocused = boxRef.current && document.activeElement === boxRef.current;
    if (!isFocused) {
      const incoming = data?.details?.title !== undefined ? data?.details?.title : data?.label;
      if (incoming !== undefined) {
        setTitle(hyphenateText(stripHtml(incoming), langCode));
      }
    }
  }, [data?.details?.title, data?.label, langCode]);

  // Handle auto focus in edit mode
  useEffect(() => {
    if (editable && autoFocus && boxRef.current) {
      setTimeout(() => {
        const el = boxRef.current;
        el.focus({ preventScroll: true });

        // Move caret to the end of the content
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        setAutoFocus(false);
      }, 0);
    }
  }, [autoFocus, editable]);

  const handleBoxClick = (e) => {
    if (editable) {
      // If the click is already on the ContentEditable, don't do anything
      if (e.target.classList.contains('nodrag')) {
        return;
      }
      if (boxRef.current && document.activeElement !== boxRef.current) {
        boxRef.current.focus();

        // Only move to end if we are explicitly focusing via click on the BOX (not text)
        const el = boxRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      if (em && em.active) {
        e.stopPropagation();
        if (em.mode === 'analysis') {
          em.onOpenPopover({ id: nodeId, title });
        }
        return;
      }
      setIsPopupVisible(true);
    }
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsPopupVisible(false);
      }
    };
    if (isPopupVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPopupVisible]);

  const handleFocus = (e) => {
    e.stopPropagation();
  };

  const handleChange = (e) => {
    const rawVal = e.target.value;
    setTitle(rawVal);
    const cleanVal = cleanHyphens(rawVal);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleBlur = () => {
    const rawVal = boxRef.current
      ? boxRef.current.innerText || boxRef.current.textContent || title
      : title;
    const cleanVal = cleanHyphens(rawVal);
    const hyphenated = hyphenateText(cleanVal, langCode);
    setTitle(hyphenated);
    if (data.onTitleChange) {
      data.onTitleChange(cleanVal);
    }
    if (data.onLabelChange) {
      data.onLabelChange(cleanVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const renderPopup = () => (
    <Draggable handle=".popupHeader" bounds=".scrollbar_wrapper" cancel=".popupContent_content">
      <ResizableBox
        width={popupSize.width}
        height={popupSize.height}
        minConstraints={[300, 200]}
        maxConstraints={maxConstraints}
        onResizeStop={(e, { size }) => setPopupSize(size)}
        style={{
          position: 'absolute',
          top: '12%',
          left: '30%',
          right: '0',
          transform: 'translate(-50%, 0)',
          backgroundColor: '#ffffff',
          border: '1px solid #011f60',
          overflow: 'hidden',
          zIndex: 1001,
          boxShadow: '0 0 10px #011f6047',
        }}
      >
        <div
          className="publishpopupdetails"
          style={{ ...styles.popup, width: '100%', height: '100%' }}
        >
          <div className="popupHeader" style={styles.popupHeader}>
            <h3 style={styles.popupTitle}>{title}</h3>
            <button style={styles.closeButton} onClick={handleClosePopup}>
              {t('CLOSE')}
            </button>
          </div>
          <div className="ql-snow" style={{ flexGrow: 1, overflowY: 'auto' }}>
            <div
              className="popupContent_content ql-editor rbpm-infobox"
              style={{ ...styles.popupContent, overflowY: 'visible' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  data.details?.translations?.[(langCode || 'EN').toUpperCase()]?.content ||
                    data.details?.translations?.[(langCode || 'en').toLowerCase()]?.content ||
                    data.details?.translations?.[langCode]?.content ||
                    data.details?.content ||
                    ''
                ),
              }}
            />
          </div>
        </div>
      </ResizableBox>
    </Draggable>
  );

  const container = document.querySelector('.ss_publish_border');

  return (
    <>
      <div
        className={editable ? 'swimboxnode_1' : 'swimboxnode_2'}
        style={styles.wrapper}
        onClick={handleBoxClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {!editable && <ActivityEnergyLayer data={data} activityId={nodeId} activityTitle={title} />}
        <div className={`borderBox ${!editable ? emClass : ''}`} style={styles.box}>
          {editable ? (
            <ContentEditable
              innerRef={boxRef}
              html={title}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => handleChange({ target: { value: e.target.value } })}
              onKeyDown={handleKeyDown}
              placeholder=""
              style={styles.label}
              className="nodrag rb-process-model-hyphenated-text"
              lang={(langCode || 'de').toLowerCase()}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div
              className="published_box_text rb-process-model-hyphenated-text"
              lang={(langCode || 'de').toLowerCase()}
              style={styles.label}
            >
              {hyphenateText(
                stripHtml(data?.details?.title || data?.label || title || ''),
                langCode
              )}
            </div>
          )}
        </div>

        {/* Top Handles */}
        {[20, 50, 80].map((leftOffset, index) => (
          <Fragment key={`top-group-${index}`}>
            <Handle
              key={`top-target-${index}`}
              type="target"
              position={Position.Top}
              id={`top-target-${index}`}
              style={
                editable && isHovered
                  ? { ...styles.hoverhandle, top: '0px', left: `${leftOffset}%` }
                  : { ...styles.handle, top: '0px', left: `${leftOffset}%` }
              }
            />
            <Handle
              key={`top-source-${index}`}
              type="source"
              position={Position.Top}
              id={`top-source-${index}`}
              style={
                editable && isHovered
                  ? { ...styles.hoverhandle, top: '0px', left: `${leftOffset}%` }
                  : { ...styles.handle, top: '0px', left: `${leftOffset}%` }
              }
            />
          </Fragment>
        ))}

        {/* Bottom Handles */}
        {[20, 50, 80].map((leftOffset, index) => (
          <Fragment key={`bottom-group-${index}`}>
            <Handle
              key={`bottom-target-${index}`}
              type="target"
              position={Position.Bottom}
              id={`bottom-target-${index}`}
              style={
                editable && isHovered
                  ? { ...styles.hoverhandle, bottom: '0px', left: `${leftOffset}%` }
                  : { ...styles.handle, bottom: '0px', left: `${leftOffset}%` }
              }
            />
            <Handle
              key={`bottom-source-${index}`}
              type="source"
              position={Position.Bottom}
              id={`bottom-source-${index}`}
              style={
                editable && isHovered
                  ? { ...styles.hoverhandle, bottom: '0px', left: `${leftOffset}%` }
                  : { ...styles.handle, bottom: '0px', left: `${leftOffset}%` }
              }
            />
          </Fragment>
        ))}

        <Handle
          type="target"
          position={Position.Left}
          id="left-target"
          style={editable && isHovered ? styles.hoverhandle : styles.handle}
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left-source"
          style={editable && isHovered ? styles.hoverhandle : styles.handle}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right-target"
          style={editable && isHovered ? styles.hoverhandle : styles.handle}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right-source"
          style={editable && isHovered ? styles.hoverhandle : styles.handle}
        />
        <div style={styles.borderOverlay}></div>
      </div>

      {!editable &&
        isPopupVisible &&
        data.details?.title &&
        container &&
        ReactDOM.createPortal(renderPopup(), container)}
    </>
  );
};

const styles = {
  wrapper: {
    position: 'relative',
    width: '80%',
    height: '72%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  box: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: '#ffffff',
    color: '#002060',
    border: '1px solid #1A192B',
    width: '100%',
    height: '100%',
    padding: '0px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  borderOverlay: {
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    right: '-2px',
    bottom: '-2px',
    zIndex: -1,
    backgroundColor: 'transparent',
    borderRadius: '5px',
    pointerEvents: 'none',
  },
  label: {
    fontSize: '12px',
    fontFamily: "'Poppins', sans-serif",
    lineHeight: '1.1',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    color: '#002060',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    width: '100%',
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  hoverhandle: {
    backgroundColor: '#FF364A',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  handle: {
    backgroundColor: 'transparent',
    border: 'none',
    width: '0px',
    height: '0px',
    pointerEvents: 'none',
  },
  popup: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    zIndex: 1001,
  },
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px',
    backgroundColor: 'white',
    cursor: 'move',
  },
  popupTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#011f60',
    margin: '0',
  },
  popupContent: {
    fontSize: '12px',
    color: '#002060',
    padding: '0 10px 10px',
    overflowY: 'auto',
    flexGrow: 1,
  },
  closeButton: {
    background: '#011f60',
    borderRadius: '7px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '2px 20px',
    textTransform: 'capitalize',
    border: '0',
  },
};

export default memo(SwimlaneBoxNode);
